import _ from 'lodash'
import { createPromise } from '@wix/thunderbolt-commons'
import type { IPlatformLogger, PublicAPI, ClientSpecMapAPI, IModelsAPI } from '@wix/thunderbolt-symbols'
import type { BootstrapData } from '../../types'
import type { IViewerHandlers } from '../types'
import {
	MODELS_API,
	CLIENT_SPEC_MAP_API,
	PLATFORM_LOGGER,
	VIEWER_HANDLERS,
	BOOTSTRAP_DATA,
	IMPORT_SCRIPTS,
	APPS_PUBLIC_API,
} from './moduleNames'

type PublicApiProviderFunc = (appDefinitionId: string) => void

export type IAppsPublicApi = {
	resolvePublicApi: (appDefinitionId: string, publicApi: PublicAPI) => void
	getPublicApi: (appDefinitionId: string) => Promise<PublicAPI>
	registerPublicApiProvider: (publicApiProviderFunc: PublicApiProviderFunc) => void
}
type PublicApisPromises = {
	[appDefinitionId: string]: { setPublicApi: (api: PublicAPI) => void; publicApiPromise: Promise<PublicAPI> }
}

const createPromiseForApp = () => {
	const { resolver, promise } = createPromise<PublicAPI>()
	return { publicApiPromise: promise, setPublicApi: resolver }
}

function resolvePublicApiMethod(obj: PublicAPI, path: string | string[]) {
	const parts = Array.isArray(path) ? path : path.split('.')
	const methodName = parts[parts.length - 1]
	if (!methodName) {
		return { methodOwner: undefined, method: undefined }
	}
	const parentPath = parts.slice(0, -1)
	// Keep the parent object so nested public API methods are called with the original `this`.
	const methodOwner = parentPath.length ? _.get(obj, parentPath) : obj
	const method = methodOwner != null ? (methodOwner as Record<string, unknown>)[methodName] : undefined
	return { methodOwner, method }
}

const AppsPublicApi = (
	modelsApi: IModelsAPI,
	clientSpecMapApi: ClientSpecMapAPI,
	logger: IPlatformLogger,
	{ viewerHandlers }: IViewerHandlers,
	bootstrapData: BootstrapData,
	importScripts: (url: string) => Promise<void>
): IAppsPublicApi => {
	const publicApisPromises: PublicApisPromises = _.mapValues(modelsApi.getApplications(), createPromiseForApp)
	let publicApiProviderFunc: PublicApiProviderFunc
	const pageId = bootstrapData.currentPageId

	async function getPublicApi(appDefinitionId: string) {
		if (!clientSpecMapApi.isAppOnSite(appDefinitionId)) {
			throw new Error(`getPublicAPI() of ${appDefinitionId} failed. The app does not exist on site.`)
		}
		if (!publicApisPromises[appDefinitionId]) {
			publicApisPromises[appDefinitionId] = createPromiseForApp()
			if (!publicApiProviderFunc) {
				logger.captureError(new Error('appsPublicApiManager Error: runApplicationFunc is not a function'), {
					tags: { appsPublicApiManager: true },
					extra: { appDefinitionId },
				})
				throw new Error(`getPublicAPI() of ${appDefinitionId} failed`)
			}
			publicApiProviderFunc(appDefinitionId) // the provider resolves the publicApisPromises[appDefinitionId] promise
		}

		return publicApisPromises[appDefinitionId].publicApiPromise
	}

	async function callRegisteredPublicApi(appDefinitionId: string, methodPath: string | string[], args?: unknown[]) {
		const publicAPI = await getPublicApi(appDefinitionId)
		const { methodOwner, method } = resolvePublicApiMethod(publicAPI, methodPath)
		if (typeof method !== 'function') {
			throw new Error(
				`publicApiCaller: "${Array.isArray(methodPath) ? methodPath.join('.') : methodPath}" does not resolve to a function on app "${appDefinitionId}"`
			)
		}

		return method.apply(methodOwner, args ?? [])
	}

	const resolvePublicApi = (appDefinitionId: string, publicApi: PublicAPI): void => {
		publicApisPromises[appDefinitionId]?.setPublicApi(publicApi)
	}

	const registerPublicApiProvider = (_publicApiProviderFunc: PublicApiProviderFunc) => {
		publicApiProviderFunc = _publicApiProviderFunc

		if (process.env.browser) {
			viewerHandlers.publicApiCaller.registerPublicApiCaller(callRegisteredPublicApi)

			viewerHandlers.publicApiTpa.registerPublicApiGetter(async () => {
				if (!self.pmrpc) {
					await logger.runAsyncAndReport('import_scripts_pm-rpc', async () => {
						try {
							await importScripts('https://static.parastorage.com/unpkg/pm-rpc@3.0.3/build/pm-rpc.min.js')
						} catch {
							await importScripts('https://static.parastorage.com/unpkg/pm-rpc@3.0.3/build/pm-rpc.min.js') // retry
						}
					})
				}

				// wix code has hard time when we call their createControllers() with no controllers
				const wixCodeAppDefinitionId = clientSpecMapApi.getWixCodeAppDefinitionId()
				const publicApis = await Promise.all(
					clientSpecMapApi
						.getAppsOnSite()
						.filter((appDefinitionId) => appDefinitionId !== wixCodeAppDefinitionId)
						.map(async (appDefinitionId) => {
							const publicAPI = await getPublicApi(appDefinitionId)
							return { appDefinitionId, publicAPI }
						})
				)

				return publicApis
					.filter(({ publicAPI }) => publicAPI)
					.map(({ appDefinitionId, publicAPI }) => {
						const name = `viewer_platform_public_api_${appDefinitionId}_${pageId}`
						self.pmrpc!.api.set(name, publicAPI)
						return name
					})
			})
		}
	}

	return {
		resolvePublicApi,
		registerPublicApiProvider,
		getPublicApi,
	}
}

export default {
	factory: AppsPublicApi,
	deps: [MODELS_API, CLIENT_SPEC_MAP_API, PLATFORM_LOGGER, VIEWER_HANDLERS, BOOTSTRAP_DATA, IMPORT_SCRIPTS],
	name: APPS_PUBLIC_API,
}
